import { useState, useRef, useEffect } from 'react';
import { Send, Loader, ImagePlus, X, Mic, MicOff, Volume2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

export default function TextChat({ user, language, voiceMode = false, selectedVoice = 'nova' }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Pre-create Audio element on first user interaction (iOS requires this)
  const audioElRef = useRef(null);
  const initAudioEl = () => {
    if (!audioElRef.current) {
      const a = new Audio();
      a.volume = 1.0;
      // Play silent audio to "unlock" the audio element for future use
      a.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwAAAAAAAAAAAAAAAAD/+xBkAA/wAABpAAAACAAADSAAAAEAAAGkAAAAIAAANIAAAAQAAABEAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
      a.play().catch(() => {});
      audioElRef.current = a;
    }
  };

  // Play AI response — reuse pre-created Audio element for iOS compatibility
  const speakResponse = async (text) => {
    if (!voiceMode) return;
    setIsSpeaking(true);
    initAudioEl();
    
    // Try backend TTS first
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      
      const res = await axios.post(`${API}/voice/tts`, {
        text: text,
        voice: selectedVoice
      }, { signal: controller.signal });
      
      clearTimeout(timeout);
      
      if (res.data.audio_base64 && audioElRef.current) {
        const audio = audioElRef.current;
        audio.src = `data:audio/mp3;base64,${res.data.audio_base64}`;
        audio.volume = 1.0;
        audio.onended = () => setIsSpeaking(false);
        audio.onerror = () => speakWithBrowser(text);
        await audio.play();
        return;
      }
    } catch (e) {
      // Fallback to browser TTS
    }
    
    speakWithBrowser(text);
  };

  const speakWithBrowser = (text) => {
    if (!('speechSynthesis' in window)) {
      setIsSpeaking(false);
      return;
    }
    window.speechSynthesis.cancel();
    
    const trySpeak = () => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        const preferred = voices.find(v => 
          v.name.includes('Samantha') || v.name.includes('Karen') || 
          v.name.includes('Google') || v.name.includes('Female') ||
          (v.lang.startsWith('en') && !v.name.includes('Male'))
        ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
        utterance.voice = preferred;
      }
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    };

    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = () => trySpeak();
      setTimeout(trySpeak, 500);
    } else {
      trySpeak();
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Pick best supported format
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : 'webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await transcribeAndSend(audioBlob, ext);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording... Tap mic again to stop');
    } catch (err) {
      console.error('Mic error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone permission denied. Please allow mic access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        toast.error('No microphone found on this device.');
      } else {
        toast.error(`Microphone error: ${err.message}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAndSend = async (audioBlob, ext = 'webm') => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording.${ext}`);
      const transcribeRes = await axios.post(`${API}/voice/transcribe`, formData);
      const text = transcribeRes.data.text;
      if (text && text.trim()) {
        setInput(text);
        await sendMessage(text);
      } else {
        toast.error('Could not understand audio. Try again.');
        setProcessing(false);
      }
    } catch {
      toast.error('Failed to transcribe audio');
      setProcessing(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only images are supported');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async (textOverride) => {
    const text = textOverride || input.trim();
    if (!text && !imageFile) return;
    if (processing && !textOverride) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: text || (imageFile ? 'Shared an image' : ''),
      image: imagePreview || null
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    const currentImage = imageFile;
    removeImage();
    setProcessing(true);

    try {
      let messageText = text;
      if (currentImage) {
        messageText = text
          ? `[User shared an image] ${text}`
          : '[User shared an image] What feelings does this image evoke? Help me process what I see.';
      }

      const response = await axios.post(`${API}/voice/text-chat`, {
        message: messageText,
        language: language || 'en',
      });

      const aiText = response.data.response;
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: aiText
      };

      setMessages(prev => [...prev, aiMessage]);

      // Speak response in voice mode
      if (voiceMode) {
        speakResponse(aiText);
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get response. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await sendMessage();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-2 min-h-0">
        {messages.length === 0 && (
          <div className="text-center text-sm text-warm-charcoal/50 italic py-6">
            {voiceMode ? 'Tap the mic to start talking...' : 'Share how you\'re feeling...'}
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl ${
              msg.type === 'user'
                ? 'bg-hunter-green text-white rounded-br-sm'
                : 'bg-stone-100 text-warm-charcoal rounded-bl-sm'
            }`}>
              {msg.image && (
                <img src={msg.image} alt="Shared" className="w-full max-h-48 object-cover rounded-t-2xl" />
              )}
              <p className="text-sm leading-relaxed p-3">{msg.text}</p>
              {msg.type === 'ai' && voiceMode && (
                <button
                  onClick={() => speakResponse(msg.text)}
                  className="px-3 pb-2 text-warm-charcoal/40 hover:text-hunter-green"
                  disabled={isSpeaking}
                >
                  <Volume2 className="w-3.5 h-3.5 inline" />
                </button>
              )}
            </div>
          </div>
        ))}
        {processing && (
          <div className="flex justify-start">
            <div className="bg-stone-100 rounded-2xl rounded-bl-sm px-4 py-3">
              <Loader className="w-4 h-4 animate-spin text-hunter-green" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Image preview */}
      {imagePreview && (
        <div className="flex-none relative inline-block mb-2">
          <img src={imagePreview} alt="Preview" className="h-16 w-16 object-cover rounded-xl border-2 border-hunter-green/30" />
          <button onClick={removeImage} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="flex-none pt-2">
        <form onSubmit={handleSubmit} className="flex items-end gap-1.5">
          {/* Image upload */}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2.5 rounded-xl text-warm-charcoal/50 hover:text-hunter-green hover:bg-stone-100 transition-colors flex-shrink-0"
            data-testid="chat-image-btn"
            style={{ touchAction: 'manipulation' }}
          >
            <ImagePlus className="w-5 h-5" />
          </button>

          {voiceMode ? (
            <>
              {/* Voice input */}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Or type here..."
                disabled={processing || isRecording}
                className="flex-1 px-3 py-2.5 rounded-xl border border-stone-200 focus:border-hunter-green focus:ring-1 focus:ring-hunter-green/20 outline-none text-sm bg-white"
              />
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={processing}
                className={`p-2.5 rounded-xl flex-shrink-0 transition-colors ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-hunter-green text-white hover:bg-hunter-green/90'
                }`}
                data-testid="chat-voice-btn"
                style={{ touchAction: 'manipulation' }}
              >
                {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            </>
          ) : (
            <>
              {/* Text input */}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type how you're feeling..."
                disabled={processing}
                className="flex-1 px-3 py-2.5 rounded-xl border border-stone-200 focus:border-hunter-green focus:ring-1 focus:ring-hunter-green/20 outline-none text-sm bg-white"
                data-testid="chat-text-input"
              />
            </>
          )}

          {/* Send button (always visible for text) */}
          <button
            type="submit"
            disabled={(!input.trim() && !imageFile) || processing}
            className="p-2.5 rounded-xl bg-hunter-green text-white hover:bg-hunter-green/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            data-testid="chat-send-btn"
            style={{ touchAction: 'manipulation' }}
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
}
